import { useQuery } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { PreviewOperationRequest } from '@/api/handlers/resources/operations/requests';
import type { PreviewOperationResponse } from '@/api/handlers/resources/operations/responses';
import type { ApiResponse } from '@/api/handlers/responses';
import { OperationsQueryKeys } from './operationsQueryKeys';

/**
 * What the operation would post — a **query**, not a mutation, even though the
 * wire verb is POST.
 *
 * The verb is POST because the body carries a slot list that will not fit in a
 * URL; the semantics are a read. Nothing is persisted, the same input always
 * answers the same way, and the form re-asks as the user types. Modelling it as
 * a mutation would give up caching, `enabled`, and the in-flight flag the panel
 * needs, and would invite an `onSuccess` that writes to state a query holds
 * already.
 *
 * **The debounce is the caller's, and it belongs on the key.** The hook is a
 * pure function of the body it is given; the Record page holds a debounced copy
 * of its form and passes that. Debouncing inside here would make the key and the
 * request disagree for a moment, which is exactly how a panel ends up showing
 * one entry while Post sends another.
 *
 * A refused preview is not an exception: it arrives as `{ success: false }` with
 * the server's code, which the form anchors to the field that caused it.
 * `retry: false` because these refusals are deterministic — a `PERIOD_CLOSED`
 * retried three times is a closed period three times, and each attempt delays
 * the message the user needs.
 */
export const useOperationPreviewQuery = (
    key: string,
    body: PreviewOperationRequest | null,
    enabled: boolean,
) => {
    const { apiClient } = useApi();

    return useQuery<ApiResponse<PreviewOperationResponse>>({
        queryKey: OperationsQueryKeys.preview(key, body),
        queryFn: () => apiClient.operations.previewOperation(key, body!),
        enabled: enabled && key.length > 0 && body !== null,
        retry: false,
        // The preview is the entry about to be posted. Serving a cached one
        // after the tenant's rates or chart moved would show figures the record
        // call will not reproduce, so it is never reused across mounts.
        staleTime: 0,
        gcTime: 0,
    });
};
